/**
 * Tutor details tab.
 * Dates, metrics, personal info, and professional-situation details.
 */
import * as React from 'react';
import {
  type LucideIcon,
  Calendar,
  BarChart3,
  TrendingUp,
  MapPin,
  CheckCircle2,
  XCircle,
  Briefcase,
} from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { DetailsCard } from '@/components/common/DetailsCard';
import { InternalCommentBox } from '@/components/common/InternalCommentBox';
import { DetailField } from '@/components/common/details/DetailField';
import {
  DEFAULT_SOURCE_CONFIG,
  PRO_SITUATION_COLOR_MAP,
  PRO_SITUATION_STATUS_LABEL,
  REGISTRATION_SOURCE_CONFIG,
  YES_NO_BADGE_CONFIG,
} from '@/components/tutors/tutor-configs';
import { flattenProfessionalSituation } from '@/utils/professional-situation-utils';
import type { TutorRecord } from '@/types/remote/tutor.types';
import {
  capitalizeFirst,
  completionBarColor,
  daysSince,
  formatCityInfo,
  formatDate,
  formatPartnerName,
  formatRegistrationManagerName,
  formatRemoteTutorProSituationDepartment,
  getRemoteTutorProSituationEnterpriseName,
  getTutorCivilServiceBranchLabel,
  getTutorFieldOfActivityLabel,
  getTutorHighSchoolCurriculumLabel,
  getTutorStudyLevelLabel,
} from '@/components/tutors/remote/tutor-table-utils';
import { useTheme } from '@/hooks/useTheme';
import { useThemeStore } from '@/stores/themeStore';
import { useQuery } from '@tanstack/react-query';
import { getUserHistory } from '@/services/history.service';

/** Yes/no badge from {@link YES_NO_BADGE_CONFIG}; null if empty. */
function renderYesNo(val: string | undefined): React.ReactNode {
  if (!val) return null;
  const cfg = YES_NO_BADGE_CONFIG[val];
  if (!cfg) return <span className="text-sm text-foreground">{val}</span>;
  return (
    <LabelBadge label={cfg.label} icon={cfg.icon} variant="outlined" color={cfg.color} size="sm" />
  );
}

/** Registration-source badge; em dash when empty. */
function SourceBadge({ source }: Readonly<{ source: string | null | undefined }>) {
  const src = source ?? '';
  const cfg = REGISTRATION_SOURCE_CONFIG[src] ?? (src ? DEFAULT_SOURCE_CONFIG : null);
  if (!cfg) return <span className="text-sm text-foreground">—</span>;
  return (
    <LabelBadge
      label={cfg.label === 'Manager' ? src : cfg.label}
      icon={cfg.icon}
      variant={cfg.badgeVariant}
      color={cfg.badgeColor}
      size="sm"
    />
  );
}

/** Professional-situation fields for a high-school student. */
function proSitHighSchool(proSit: any): React.ReactNode[] {
  const fields: React.ReactNode[] = [];
  if (proSit.establishment || proSit.establishmentName) {
    fields.push(
      <DetailField
        key="establishment"
        label="Établissement"
        value={proSit.establishmentName || proSit.establishment}
      />,
    );
  }
  if (proSit.level) {
    fields.push(
      <DetailField
        key="level"
        label="Niveau d'études"
        value={getTutorStudyLevelLabel(proSit.level)}
      />,
    );
  }
  if (proSit.curriculum) {
    fields.push(
      <DetailField
        key="curriculum"
        label="Cursus scolaire"
        value={getTutorHighSchoolCurriculumLabel(proSit.curriculum)}
      />,
    );
  }
  if (proSit.associativeEngagement) {
    fields.push(
      <DetailField
        key="engagement"
        label="Engagement associatif"
        value={renderYesNo(proSit.associativeEngagement)}
      />,
    );
  }
  return fields;
}

/** Professional-situation fields for a student. */
function proSitStudent(proSit: any): React.ReactNode[] {
  const fields: React.ReactNode[] = [];
  if (proSit.level) {
    fields.push(
      <DetailField
        key="level"
        label="Niveau d'études"
        value={getTutorStudyLevelLabel(proSit.level)}
      />,
    );
  }
  if (proSit.fieldOfStudy) {
    fields.push(
      <DetailField key="fieldOfStudy" label="Domaine d'études" value={proSit.fieldOfStudy} />,
    );
  }
  if (proSit.establishment || proSit.establishmentName) {
    fields.push(
      <DetailField
        key="establishment"
        label="Établissement"
        value={proSit.establishmentName || proSit.establishment}
      />,
    );
  }
  if (proSit.associativeEngagement) {
    fields.push(
      <DetailField
        key="associativeEngagement"
        label="Engagement associatif"
        value={renderYesNo(proSit.associativeEngagement)}
      />,
    );
  }
  return fields;
}

/** Professional-situation fields for a worker. */
function proSitWorker(
  proSit: any,
  departments: { code: string; name: string }[],
): React.ReactNode[] {
  const fields: React.ReactNode[] = [];
  if (proSit.fieldOfActivity) {
    fields.push(
      <DetailField
        key="fieldOfActivity"
        label="Secteur d'activité"
        value={getTutorFieldOfActivityLabel(proSit.fieldOfActivity)}
      />,
    );
  }
  if (proSit.activityDepartment) {
    fields.push(
      <DetailField
        key="activityDepartment"
        label="Département d'activité"
        value={formatRemoteTutorProSituationDepartment(proSit.activityDepartment, departments)}
      />,
    );
  }
  if (proSit.civilServantBranch) {
    fields.push(
      <DetailField
        key="civilServantBranch"
        label="Grande fonction publique"
        value={getTutorCivilServiceBranchLabel(proSit.civilServantBranch)}
      />,
    );
  }
  const enterpriseName = getRemoteTutorProSituationEnterpriseName(proSit);
  if (enterpriseName) {
    fields.push(
      <DetailField key="companyName" label="Organisme / Administration" value={enterpriseName} />,
    );
  }
  if (proSit.isCompanyPartner) {
    const partnerCompanyName = useThemeStore.getState().companyName;
    fields.push(
      <DetailField
        key="isCompanyPartner"
        label={
          partnerCompanyName
            ? `Entreprise partenaire ${partnerCompanyName}`
            : 'Entreprise partenaire'
        }
        value={renderYesNo(proSit.isCompanyPartner)}
      />,
    );
  }
  if (proSit.isEngagementValorised) {
    fields.push(
      <DetailField
        key="isEngagementValorised"
        label="Engagement valorisé"
        value={renderYesNo(proSit.isEngagementValorised)}
      />,
    );
  }
  return fields;
}

/** Flattened professional-situation fields for the details / registration tabs. */
export function renderProfessionalSituationDetails(
  proSitRaw: any,
  departments: { code: string; name: string }[] = [],
): React.ReactNode {
  const proSit = flattenProfessionalSituation(proSitRaw);
  if (!proSit?.status) return null;

  let fields: React.ReactNode[] = [];
  if (proSit.status === 'high_school_student') {
    fields = proSitHighSchool(proSit);
  } else if (proSit.status === 'student') {
    fields = proSitStudent(proSit);
  } else if (proSit.status === 'employee' || proSit.status === 'civil_servant') {
    fields = proSitWorker(proSit, departments);
  } else if (proSit.status === 'unemployed' || proSit.status === 'retired') {
    if (proSit.fieldOfActivity) {
      fields = [
        <DetailField
          key="fieldOfActivity"
          label="Secteur d'activité"
          value={getTutorFieldOfActivityLabel(proSit.fieldOfActivity)}
        />,
      ];
    }
  }

  return fields.length > 0 ? <>{fields}</> : null;
}

/** Props for {@link TutorDetailsTab}. */
interface TutorDetailsTabProps {
  record: TutorRecord;
  displayRecord: TutorRecord;
  sections: Record<string, unknown>;
  completion: TutorRecord['completion'];
  departments: { code: string; name: string }[];
  statusConfig:
    | { label: string; icon: LucideIcon; badgeVariant: any; badgeColor?: string }
    | undefined;
  assignabilityConfig: { label: string; icon: LucideIcon; badgeVariant: any; badgeColor?: string };
}

/** Tutor details tab. */
export const TutorDetailsTab: React.FC<TutorDetailsTabProps> = ({
  record,
  displayRecord,
  sections,
  completion,
  departments,
  statusConfig,
  assignabilityConfig,
}) => {
  const { companyName } = useTheme();
  const isArchived = record.user?.status === 'archived';
  const { data: history } = useQuery({
    queryKey: ['history', 'user', record.userId],
    queryFn: () => getUserHistory(record.userId),
    enabled: isArchived && !!record.userId,
  });
  const archive = history?.archive;
  const archiveReason =
    archive?.comment && archive.reason
      ? `${archive.reason} — ${archive.comment}`
      : (archive?.reason ?? archive?.comment ?? '—');

  return (
    <div className="details-view-grid mt-6">
      <DetailsCard icon={Calendar} title="Dates" colorName="color-2" colSpan={1}>
        <div className="space-y-3">
          <DetailField label="Date d'inscription" value={formatDate(record.createdAt)} />
          <DetailField label="Ancienneté" value={daysSince(record.createdAt)} />
          <DetailField
            label="Fin d'engagement"
            value={formatDate((sections['my_support'] as any)?.engagementEndDate)}
          />
          {isArchived && archive ? (
            <DetailField label="Date d'archivage" value={formatDate(archive.archivedAt)} />
          ) : null}
        </div>
      </DetailsCard>

      <DetailsCard icon={BarChart3} title="Métriques" colorName="color-1" colSpan={1}>
        <div className="space-y-3">
          <DetailField
            label="Statut du tuteur"
            value={
              statusConfig ? (
                <LabelBadge
                  label={statusConfig.label}
                  icon={statusConfig.icon}
                  variant={statusConfig.badgeVariant}
                  color={statusConfig.badgeColor}
                  size="sm"
                />
              ) : (
                record.onboardingStatus
              )
            }
          />
          {isArchived && archive ? (
            <>
              <DetailField label="Archivé par" value={archive.archivedBy?.userFullName ?? '—'} />
              <DetailField label="Motif" value={archiveReason} />
            </>
          ) : null}
          <DetailField
            label="Collecté via"
            value={
              formatRegistrationManagerName(displayRecord) === '—' ? (
                '—'
              ) : (
                <LabelBadge
                  label={formatRegistrationManagerName(displayRecord)}
                  icon={statusConfig?.icon}
                  variant="outlined"
                  color="rgb(var(--color-1-nuance-1))"
                  size="sm"
                />
              )
            }
          />
          <DetailField
            label="Source"
            value={
              <SourceBadge
                source={displayRecord.source ?? displayRecord.user?.registrationSource}
              />
            }
          />
          <DetailField
            label="Binômes"
            value={(displayRecord.user?.binomes?.length ?? 0).toString()}
          />
          <DetailField
            label="Affectable"
            value={
              <LabelBadge
                label={assignabilityConfig.label}
                icon={assignabilityConfig.icon}
                variant={assignabilityConfig.badgeVariant}
                color={assignabilityConfig.badgeColor}
                size="sm"
              />
            }
          />
          <DetailField label="Note" value="—" />
        </div>
      </DetailsCard>

      <DetailsCard icon={TrendingUp} title="Progression" colorName="color-3" colSpan={2}>
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Blocs onboarding</span>
              <span className="text-xs text-muted-foreground">
                {completion.completedSections}/{completion.totalSections} sections
              </span>
            </div>
            <div className="w-full h-2 bg-[var(--color-1-nuance-3)] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${completion.completedSectionsPercentage}%`,
                  backgroundColor: completionBarColor(completion.completedSectionsPercentage),
                }}
              />
            </div>
            <span className="text-xs text-muted-foreground mt-1 block">
              {completion.completedSectionsPercentage}%
            </span>
          </div>

          {(() => {
            const current = displayRecord.user?.binomes?.length ?? 0;
            const max = (sections['my_support'] as any)?.numberOfStudentsToSupport ?? 1;
            const pct = Math.min((current / max) * 100, 100);
            return (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">Capacité</span>
                  <span className="text-xs text-muted-foreground">
                    {current} / {max}
                  </span>
                </div>
                <div className="w-full h-2 bg-[var(--color-1-nuance-3)] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${pct}%`,
                      backgroundColor: completionBarColor(pct),
                    }}
                  />
                </div>
              </div>
            );
          })()}

          {(() => {
            const saisis = displayRecord.user?.comptesRendusSaisisCount ?? 0;
            return (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">Comptes rendus</span>
                  <span className="text-xs text-muted-foreground">
                    {saisis} saisi{saisis !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="w-full h-2 bg-[var(--color-1-nuance-3)] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: saisis > 0 ? '100%' : '0%',
                      backgroundColor: saisis > 0 ? completionBarColor(100) : '#9CA3AF',
                    }}
                  />
                </div>
              </div>
            );
          })()}
        </div>
      </DetailsCard>

      <DetailsCard icon={MapPin} title="Informations personnelles" colorName="color-1" colSpan={2}>
        {(() => {
          const d = sections['my_information'] as any;
          if (!d) {
            return <p className="text-sm text-muted-foreground">Aucune donnée</p>;
          }
          const proSit = flattenProfessionalSituation(d.professionalSituation);
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
              <DetailField label="Adresse" value={d.address} />
              <DetailField label="Ville" value={formatCityInfo(d.zipCode, d.city)} />
              <DetailField label="Pays" value={capitalizeFirst(d.country)} />
              <DetailField label="Département" value={d.department?.departmentName} />
              <DetailField
                label="Date de naissance"
                value={
                  d.dateOfBirth
                    ? `${formatDate(d.dateOfBirth)} (${Math.floor((Date.now() - new Date(d.dateOfBirth).getTime()) / (1000 * 60 * 60 * 24 * 365))} ans)`
                    : '—'
                }
              />
              <DetailField
                label={`Partenaire ${companyName}`}
                value={
                  <LabelBadge
                    label={d.isPartner ? 'Partenaire' : 'Non partenaire'}
                    icon={d.isPartner ? CheckCircle2 : XCircle}
                    variant="outlined"
                    color={d.isPartner ? 'rgb(var(--color-success))' : 'rgb(var(--color-error))'}
                    size="sm"
                  />
                }
              />
              {d.isPartner && (
                <DetailField label="Nom partenaire" value={formatPartnerName(d.partner)} />
              )}
              {proSit?.status && PRO_SITUATION_COLOR_MAP[proSit.status] && (
                <>
                  <div className="md:col-span-2 pt-2 border-t border-border">
                    <DetailField
                      label="Situation professionnelle"
                      value={
                        <LabelBadge
                          label={PRO_SITUATION_STATUS_LABEL[proSit.status] ?? proSit.status}
                          icon={Briefcase}
                          variant="outlined"
                          color={
                            PRO_SITUATION_COLOR_MAP[proSit.status] ?? 'rgb(var(--color-1-nuance-1))'
                          }
                          size="sm"
                        />
                      }
                    />
                  </div>
                  {renderProfessionalSituationDetails(proSit, departments)}
                </>
              )}
            </div>
          );
        })()}
      </DetailsCard>
      <InternalCommentBox userId={record.userId} comment={record.user?.internalComment ?? null} />
    </div>
  );
};
